import { Formik, Form } from 'formik';
import type { FormikValues } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { PreferredUnitSystem } from '@frontend/api';
import {
  Box,
  Button,
  Center,
  Flex,
  Divider,
  DrawerBody,
  DrawerFooter,
  Spinner,
  Tooltip,
} from '@frontend/legacy-components';

import {
  FormHeader,
  NestSettings,
  PricingSettings,
  StockProperties,
} from 'modules/common/components/nestingDrawers';
import type { SheetNestingDefaultsData } from 'modules/common/components/nestingDrawers';
import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useFeatureFlagValue } from 'store/session/hooks';
import { getGroupId, getPreferredUnitSystem } from 'store/session/selectors';

import { useSheetNestingDefaults, useUpdateSheetNestingDefaults } from './';
import { drawer } from './data/actions';

// Update formik values to match format for POST payload
const getNestFormPayload = (values: FormikValues) => {
  return {
    ...(values as SheetNestingDefaultsData),
    stockLength: values.stockLength,
    stockWidth: values.stockWidth,
    costDollars: values.costDollars ? Number(values.costDollars) : null,
    partToPartClearance: values.partToPartClearance,
    edgeBuffer: values.edgeBuffer,
    kerfWidth: values.kerfWidth,
    nestingDirection: values.nestingDirection || null,
    dropThresholdPct: values.dropThresholdPct
      ? Number(values.dropThresholdPct)
      : null,
  };
};

export const SheetNestingDefaultsForm = () => {
  const dispatch = useDispatch();
  const locator = useSelector(getGroupId);
  const unitSystem: PreferredUnitSystem = useSelector(getPreferredUnitSystem);

  const handleClose = () => {
    dispatch(drawer.close());
  };
  const { data, isLoading: isCurrentDefaultsLoading } = useSheetNestingDefaults(
    { locator },
    {},
  );

  const { mutate: updateSheetNestDefaults, isLoading: isSubmitting } =
    useUpdateSheetNestingDefaults({ locator }, {});

  const isErp = useFeatureFlagValue(featureFlagKeys.isErpCodeAvailable);

  if (isCurrentDefaultsLoading || !data) {
    return (
      <Center h="100%">
        <Spinner size="md" />
      </Center>
    );
  }

  const processedData = {
    ...data,
    costDollars: data.costDollars?.toString() ?? '',
    nestingDirection: data.nestingDirection || '',
    dropThresholdPct: data.dropThresholdPct?.toString() ?? '',
  };

  return (
    <>
      <Formik
        validationSchema={() =>
          Yup.lazy(() => {
            return Yup.object().shape({
              stockLength: Yup.string().nullable(),
              stockWidth: Yup.string().nullable(),
              grainDirection: Yup.string().nullable(),
              erpCode: Yup.string(),
              costDollars: Yup.string(),
              costingUnit: Yup.string().required(),
              nestBehaviorPartInPart: Yup.boolean().required(),
              nestBehaviorCommonLine: Yup.boolean().required(),
              partToPartClearance: Yup.string().nullable(),
              edgeBuffer: Yup.string().nullable(),
              kerfWidth: Yup.string().nullable(),
              nestingDirection: Yup.string(),
              costDistMethod: Yup.string(),
              dropThresholdPct: Yup.string(),
            });
          })
        }
        initialValues={processedData}
        onSubmit={async (values) => {
          const payload = getNestFormPayload(values);
          updateSheetNestDefaults(payload);
        }}
        validateOnMount={true}
        enableReinitialize={true}
      >
        {(formik) => {
          return (
            <Box
              as={Form}
              minH={0}
              display={'flex'}
              flexDir={'column'}
              flexGrow={1}
            >
              <FormHeader isLinearNest={false} />
              <DrawerBody flexGrow={1} minH={0} overflowY={'auto'}>
                <StockProperties
                  isSubmitting={isSubmitting}
                  isErp={isErp}
                  unitSystem={unitSystem}
                />
                <Divider
                  orientation="horizontal"
                  variant="solid"
                  marginTop={3}
                  marginBottom={2}
                />
                <NestSettings
                  isSubmitting={isSubmitting}
                  isLinearNest={false}
                  initialPartToPartClearance={processedData.partToPartClearance}
                  unitSystem={unitSystem}
                />
                <Divider
                  orientation="horizontal"
                  variant="solid"
                  marginTop={3}
                  marginBottom={2}
                />
                <PricingSettings isSubmitting={isSubmitting} />
              </DrawerBody>
              <DrawerFooter
                gap={1}
                flexDirection="column"
                alignItems="flex-end"
              >
                <Flex gap={1}>
                  <Button
                    size="lg"
                    variant="secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      handleClose();
                    }}
                    isDisabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Tooltip
                    isDisabled={isSubmitting || formik.dirty}
                    hasArrow={true}
                    label="No changes to save"
                    placement="top"
                  >
                    <Button
                      colorScheme="default"
                      size="lg"
                      variant="primary"
                      type="submit"
                      isDisabled={!formik.isValid || !formik.dirty}
                      isLoading={isSubmitting}
                    >
                      Save Changes
                    </Button>
                  </Tooltip>
                </Flex>
              </DrawerFooter>
            </Box>
          );
        }}
      </Formik>
    </>
  );
};
